import { FamilyLineItem, LineItem } from "../../lib/models/LineItems";
import { formatMoney, formatMoneyShort } from "../../util/strings";

import { FONT_SETTINGS } from "../../fonts";
import FoundryLink from "../../components/FoundryLink";
import { FoundryOrder } from "../../lib/foundry_client/models/FoundryOrder";
import { PRODUCTS_MAP } from "../../lib/models/Products";
import PhysicalLineItemSection from "./PhysicalLineItem";
import { PhysicalProductsContext } from "../../contexts/PhysicalProductsContext";
import Routes from "../../routes";
import { useContext } from "react";
import usePhysicalProducts from "../../hooks/usePhysicalProducts";

interface Props {
  order: FoundryOrder;
}

function FontSection({ items }: { items: FamilyLineItem[] }) {
  return (
    <section>
      <h3 className="mb-2 t5">Fonts</h3>
      {items.map((family: FamilyLineItem) => {
        const font = FONT_SETTINGS[family.familyName];
        return (
          <>
            <hr className="my-4 w-full flex-row items-center justify-center" />
            <div className="flex flex-row gap-8">
              <FoundryLink
                href={Routes.fonts.add.styles(family.familyName)}
                className={`w-24 h-24 bg-${font.accentColor} flex items-center justify-center t2 rounded-lg color-blue-hover`}
                style={{ fontFamily: PRODUCTS_MAP[family.items[0].id].cssName }}
              >
                {family.familyName.substring(0, 2)}
              </FoundryLink>

              <div className="flex flex-col flex-grow">
                <h4>{family.familyName}</h4>
                <p className="mt-0">
                  Licensed platforms: {family.platforms?.join(", ")}
                </p>
                <div className="border-l-2 pl-2">
                  {family.items.map((item) => {
                    return (
                      <div className="flex flex-row justify-between">
                        <h4
                          style={{
                            fontFamily: PRODUCTS_MAP[item.id].cssName,
                          }}
                        >
                          {item.name}
                        </h4>
                        {/* TODO: combine this with FontFamilySection */}
                        <div>
                          <span
                            className={`${
                              item.discountPercentage > 0
                                ? "line-through opacity-50 ml-4"
                                : "ml-4"
                            }`}
                          >
                            {formatMoneyShort("USD", item.price)}
                          </span>
                          {item.discountPercentage > 0 && (
                            <span className="ml-4 color-green">
                              {formatMoneyShort(
                                "USD",
                                (1.0 - item.discountPercentage) * item.price
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        );
      })}
    </section>
  );
}

function PhysicalLineItemsSection({ items }: { items: LineItem[] }) {
  const { physicalProducts } = useContext(PhysicalProductsContext);
  usePhysicalProducts();

  return (
    <section>
      <h3 className="mb-2 t5">Physical Products</h3>

      {items.map((item: LineItem) => {
        const productDetails = physicalProducts?.find((p) => p.id === item.id);
        return (
          <>
            <hr className="my-4 w-full flex-row items-center justify-center" />
            <PhysicalLineItemSection
              item={item}
              productDetails={productDetails}
            />
          </>
        );
      })}
    </section>
  );
}

function Summary({
  order,
  hasPhysicalItems,
}: {
  order: FoundryOrder;
  hasPhysicalItems: boolean;
}) {
  return (
    <section>
      <h3 className="mb-2 t5">Summary</h3>
      <hr className="mb-4 w-full flex-row items-center justify-center" />
      <div className="mt-4 mb-2 flex flex-row justify-between gap-1">
        <span>Subtotal</span>
        <span>{formatMoney("USD", order.amountSubtotal)}</span>
      </div>
      {order.amountDiscount ? (
        <div className="mb-2 flex flex-row justify-between color-green">
          <span>Discounts</span>
          <span>-{formatMoney("USD", order.amountDiscount)}</span>
        </div>
      ) : (
        <></>
      )}
      {hasPhysicalItems && (
        <div className="flex flex-row justify-between mb-2 gap-1">
          <span>Shipping</span>
          <span className="opacity-50">
            {!order.amountShipping
              ? "free ✿"
              : formatMoney("USD", order.amountShipping)}
          </span>
        </div>
      )}
      <div className="flex flex-row justify-between mb-4 gap-1">
        <span>Tax</span>
        <span className="opacity-50">
          {formatMoney("USD", order.amountTax || 0)}
        </span>
      </div>
      <hr className="my-1" />
      <div className="my-4 flex flex-row justify-between">
        <span>Total</span>
        <span>{formatMoney("USD", order.amountPaid || 0)}</span>
      </div>
    </section>
  );
}

export default function OrderBreakdown(props: Props) {
  const fontItems = props.order.lineItems.filter(
    (lineItem) => "familyName" in lineItem
  );

  const physicalItems = props.order.lineItems.filter(
    (lineItem) => !("familyName" in lineItem)
  ) as LineItem[];

  return (
    <div className="flex flex-col gap-12">
      {fontItems.length > 0 && <FontSection items={fontItems}></FontSection>}
      {physicalItems.length > 0 && (
        <PhysicalLineItemsSection
          items={physicalItems}
        ></PhysicalLineItemsSection>
      )}
      <Summary
        order={props.order}
        hasPhysicalItems={physicalItems.length > 0}
      ></Summary>
    </div>
  );
}
